const CUSTOMER_DISPLAY_SNAPSHOT_KEY = 'pre_invoice_customer_display_snapshot'
const CUSTOMER_DISPLAY_CHANNEL = 'pre-invoice-customer-display'
const CUSTOMER_DISPLAY_ACTIVITY_EVENTS = ['click', 'keydown', 'mousemove', 'mousedown', 'touchstart', 'wheel', 'scroll']
const CUSTOMER_DISPLAY_IDLE_TIMEOUT_MS = 30000
const CUSTOMER_DISPLAY_PUBLISH_DELAY_MS = 120

export default {
  data() {
    return {
      customerDisplayPublishTimer: null,
      customerDisplayIdleTimer: null,
      customerDisplayReady: false,
      customerDisplayIdle: false
    }
  },
  watch: {
    model: {
      deep: true,
      handler() {
        this.scheduleCustomerDisplayPublish()
      }
    },
    selectedEnrollment() {
      this.scheduleCustomerDisplayPublish()
    },
    khqrDisplay() {
      this.scheduleCustomerDisplayPublish()
    },
    khqrStatus() {
      this.scheduleCustomerDisplayPublish()
    }
  },
  beforeDestroy() {
    this.stopCustomerDisplaySession()
  },
  methods: {
    startCustomerDisplaySession() {
      this.registerCustomerDisplayListener()
      this.registerCustomerDisplayActivityListeners()
      window.addEventListener('beforeunload', this.onBeforeUnload)
    },
    markCustomerDisplayReady() {
      this.customerDisplayReady = true
      this.publishCustomerDisplay()
      this.resetCustomerDisplayIdleTimer()
    },
    stopCustomerDisplaySession() {
      this.unregisterCustomerDisplayListener()
      this.unregisterCustomerDisplayActivityListeners()
      window.removeEventListener('beforeunload', this.onBeforeUnload)
      if (this.customerDisplayPublishTimer) window.clearTimeout(this.customerDisplayPublishTimer)
      if (this.customerDisplayIdleTimer) window.clearTimeout(this.customerDisplayIdleTimer)
      this.customerDisplayPublishTimer = null
      this.customerDisplayIdleTimer = null
      this.clearCustomerDisplay()
    },
    onBeforeUnload(event) {
      if (!this.isKhqrPending) return undefined
      event.preventDefault()
      event.returnValue = ''
      return ''
    },
    registerCustomerDisplayListener() {
      window.addEventListener('customer-display:open', this.onCustomerDisplayOpen)
    },
    unregisterCustomerDisplayListener() {
      window.removeEventListener('customer-display:open', this.onCustomerDisplayOpen)
    },
    registerCustomerDisplayActivityListeners() {
      CUSTOMER_DISPLAY_ACTIVITY_EVENTS.forEach(eventName => {
        window.addEventListener(eventName, this.onCustomerDisplayUserActivity, { passive: true })
      })
    },
    unregisterCustomerDisplayActivityListeners() {
      CUSTOMER_DISPLAY_ACTIVITY_EVENTS.forEach(eventName => {
        window.removeEventListener(eventName, this.onCustomerDisplayUserActivity)
      })
    },
    onCustomerDisplayOpen() {
      this.onCustomerDisplayUserActivity()
    },
    onCustomerDisplayUserActivity() {
      if (!this.customerDisplayReady) return

      if (this.customerDisplayIdle) {
        this.customerDisplayIdle = false
        this.publishCustomerDisplay()
      }

      this.resetCustomerDisplayIdleTimer()
    },
    resetCustomerDisplayIdleTimer() {
      if (!this.customerDisplayReady) return
      if (this.customerDisplayIdleTimer) window.clearTimeout(this.customerDisplayIdleTimer)
      this.customerDisplayIdleTimer = window.setTimeout(() => {
        this.customerDisplayIdleTimer = null
        this.publishCustomerDisplayIdle()
      }, CUSTOMER_DISPLAY_IDLE_TIMEOUT_MS)
    },
    publishCustomerDisplayIdle() {
      if (!this.customerDisplayReady) return

      this.customerDisplayIdle = true
      this.publishCustomerDisplay()
    },
    clearCustomerDisplay() {
      window.localStorage.removeItem(CUSTOMER_DISPLAY_SNAPSHOT_KEY)
      this.publishCustomerDisplayMessage('clear', null)
    },
    publishCustomerDisplayMessage(type, payload) {
      const message = {
        version: 'v1',
        type,
        source_tab_id: 'pre-invoice-v2',
        sent_at: Date.now(),
        payload
      }
      if (typeof window.BroadcastChannel === 'function') {
        const channel = new window.BroadcastChannel(CUSTOMER_DISPLAY_CHANNEL)
        channel.postMessage(message)
        channel.close()
      }
      window.postMessage(message, window.location.origin)
    },
    publishCustomerDisplayPaymentSuccess() {
      const payload = this.buildCustomerDisplaySnapshot()
      if (!payload.payment_success) return
      this.publishCustomerDisplayMessage('payment_success', payload)
    },
    scheduleCustomerDisplayPublish() {
      if (!this.customerDisplayReady) return
      if (this.customerDisplayPublishTimer) window.clearTimeout(this.customerDisplayPublishTimer)
      this.customerDisplayPublishTimer = window.setTimeout(() => {
        this.customerDisplayPublishTimer = null
        this.publishCustomerDisplay()
      }, CUSTOMER_DISPLAY_PUBLISH_DELAY_MS)
    },
    buildCustomerDisplaySnapshot() {
      const rows = this.model.items.map(item => {
        const quantity = Number(item.quantity || 0)
        const amount = Number(item.amount_include_vat || 0)
        return {
          fee_type: this.customerDisplayFeeTypeName(item),
          fee_name: item.fee_description || '',
          qty: quantity,
          unit_price: quantity > 0 ? amount / quantity : amount,
          sub_total: amount
        }
      })
      const paymentSuccess = this.customerDisplayPaymentSuccess()

      return {
        version: 'v1',
        active: !this.customerDisplayIdle,
        source: 'pre_invoice',
        pre_invoice_number: this.khqrSourceId ? String(this.khqrSourceId) : '',
        pre_invoice_date: this.model.pre_inv_date,
        branch_name: this.customerDisplayBranchName(),
        student_name: this.customerDisplayStudentName(),
        student_name_kh: this.customerDisplayStudentNameKh(),
        first_name_en: this.customerDisplayStudentField('first_name_en'),
        last_name_en: this.customerDisplayStudentField('last_name_en'),
        first_name_kh: this.customerDisplayStudentField('first_name_kh'),
        last_name_kh: this.customerDisplayStudentField('last_name_kh'),
        card_number: this.selectedEnrollment ? this.selectedEnrollment.card_number : '',
        photo: this.customerDisplayStudentPhoto(),
        phone: this.customerDisplayStudentPhone(),
        gender: this.customerDisplayStudentGender(),
        dob: this.customerDisplayStudentDob(),
        grade: this.customerDisplayStudentGrade(),
        academic: this.customerDisplayAcademicInfo(),
        rows,
        items: rows,
        summary: {
          subtotal: this.totalAmount,
          total: this.totalAmount,
          paid: this.khqrStatus === 'paid' ? this.totalAmount : 0,
          due: this.khqrStatus === 'paid' ? 0 : this.totalAmount
        },
        total_amount: this.totalAmount,
        khqr: this.khqrDisplay,
        payment_success: paymentSuccess,
        locale: this.$i18n ? this.$i18n.locale : null,
        updated_at: new Date().toISOString()
      }
    },
    publishCustomerDisplay() {
      const snapshot = this.buildCustomerDisplaySnapshot()
      window.localStorage.setItem(CUSTOMER_DISPLAY_SNAPSHOT_KEY, JSON.stringify(snapshot))
      this.publishCustomerDisplayMessage('snapshot', snapshot)
    },
    customerDisplayBranchName() {
      const branch = this.$store.state.authUser ? this.$store.state.authUser.default_branch : null
      if (typeof branch === 'string') return branch
      return branch ? (branch.name_kh || branch.branch_name_native || branch.name || branch.branch_name_foreign || '') : ''
    },
    customerDisplayStudentField(field) {
      const student = this.selectedEnrollment || this.model.student || {}
      return student[field] || ''
    },
    customerDisplayStudentName() {
      if (this.selectedEnrollment) {
        const enName = [this.selectedEnrollment.last_name_en, this.selectedEnrollment.first_name_en].filter(Boolean).join(' ')
        const khName = [this.selectedEnrollment.last_name_kh, this.selectedEnrollment.first_name_kh].filter(Boolean).join(' ')
        return enName || khName
      }
      const student = this.model.student || {}
      const enName = [student.last_name_en, student.first_name_en].filter(Boolean).join(' ')
      const khName = [student.last_name_kh, student.first_name_kh].filter(Boolean).join(' ')
      return enName || khName
    },
    customerDisplayStudentNameKh() {
      if (this.selectedEnrollment) {
        return [this.selectedEnrollment.last_name_kh, this.selectedEnrollment.first_name_kh].filter(Boolean).join(' ')
      }
      const student = this.model.student || {}
      return [student.last_name_kh, student.first_name_kh].filter(Boolean).join(' ')
    },
    customerDisplayPaymentSuccess() {
      if (this.khqrStatus !== 'paid') return null

      const billNumber = this.khqrDisplay && (this.khqrDisplay.bill_number || this.khqrDisplay.reference || this.khqrDisplay.payment_attempt_id)
      return {
        key: [billNumber, this.khqrSourceId].filter(Boolean).join(':') || String(Date.now()),
        name: this.customerDisplayStudentName() || this.customerDisplayStudentNameKh(),
        name_kh: this.customerDisplayStudentNameKh(),
        amount: Number((this.khqrDisplay && this.khqrDisplay.amount) || this.totalAmount || 0),
        currency: (this.khqrDisplay && this.khqrDisplay.currency_code) || 'USD',
        updated_at: new Date().toISOString()
      }
    },
    customerDisplayStudentPhoto() {
      const student = this.selectedEnrollment || this.model.student || {}
      return student.photo || student.photo_url || student.student_photo || ''
    },
    customerDisplayStudentPhone() {
      const student = this.selectedEnrollment || this.model.student || {}
      return student.phone || student.phone_number || student.tel || student.contact_number || ''
    },
    customerDisplayStudentGender() {
      const student = this.selectedEnrollment || this.model.student || {}
      return student.gender || student.sex || student.gender_name || ''
    },
    customerDisplayStudentDob() {
      const student = this.selectedEnrollment || this.model.student || {}
      return student.dob || student.date_of_birth || student.birth_date || student.student_dob || ''
    },
    customerDisplayStudentGrade() {
      const enrollment = this.selectedEnrollment || {}
      return enrollment.grade_name_foreign || this.customerDisplayOptionName(this.grades, 'grade_id', this.model.grade_id, 'grade_name_foreign')
    },
    customerDisplayAcademicInfo() {
      const enrollment = this.selectedEnrollment || {}
      return {
        main_program: enrollment.acp_name_foreign || this.customerDisplayOptionName(this.mainPrograms, 'acp_id', this.model.acp_id, 'acp_name_foreign'),
        sub_program: enrollment.acps_name_foreign || this.customerDisplayOptionName(this.subPrograms, 'acps_id', this.model.acps_id, 'acps_name_foreign'),
        term: enrollment.term_name || this.customerDisplayOptionName(this.academicTerms, 'ac_term_id', this.model.ac_term_id, 'term_name'),
        grade: enrollment.grade_name_foreign || this.customerDisplayOptionName(this.grades, 'grade_id', this.model.grade_id, 'grade_name_foreign'),
        class_name: enrollment.class_name || '',
        teacher: enrollment.teacher_name_foreign || enrollment.teacher_name_native || '',
        study_time: enrollment.study_time || this.customerDisplayOptionName(this.studyTimes, 'ac_st_id', this.model.study_time_id, 'study_time'),
        academic_status: this.customerDisplayAcademicStatus(enrollment)
      }
    },
    customerDisplayOptionName(options, idKey, value, labelKey) {
      const item = (options || []).find(option => String(option[idKey]) === String(value))
      return item ? item[labelKey] : ''
    },
    customerDisplayAcademicStatus(record) {
      const value = record.ac_status || record.ac_status_id || record.academic_status || ''
      if (String(value) === '1') return 'Active'
      return value ? String(value) : ''
    },
    customerDisplayFeeTypeName(item) {
      const feeType = this.feeTypes.find(type => String(type.fee_type_id) === String(item.fee_type_id))
      return feeType ? feeType.fee_type_name : ''
    }
  }
}
